import {PROFICIENCY_LEVELS} from 'client/AdaptiveSubjects/constants';

import type {ProficiencyMethod} from './contentFilters';

export interface ProficiencySegment {
  /**
   * Slot in the shared five-segment array. Diagnostics start at 1 because they have
   * no Not-started bucket, and sharing the slots keeps colors, icons, and popover
   * indexing identical across methods.
   */
  index: number;
  label: string;
  /**
   * Title-cased wording used by the sort menu and the summary donut. Only Not started
   * differs from `label`, which is the proficiency chart's spelling.
   */
  formalLabel?: string;
  /** Metric keys read in order; the first defined value wins. */
  metricKeys: string[];
  /** The date-bounded metric the summary donut reads. */
  summaryKey: string;
  sortKey: string;
}

interface ProficiencyMethodSegments {
  segments: ProficiencySegment[];
  /**
   * Practice reports carry an explicit denominator. Diagnostics have none, so their
   * total is the sum of the segments.
   */
  totalMetricKey?: string;
}

const LEVELS = [0, 1, 2, 3] as const;

/**
 * The proficiency buckets each measurement method reports, and the metrics behind
 * them. Sort options, chart segments, and the summary donut all read from here so a
 * level can't drift between them.
 */
export const PROFICIENCY_SEGMENTS_BY_METHOD: Record<ProficiencyMethod, ProficiencyMethodSegments> =
  {
    practice: {
      totalMetricKey: 'count_student_proficiencies',
      segments: [
        {
          index: 0,
          label: 'Not started',
          formalLabel: 'Not Started',
          metricKeys: [
            'count_unpracticed_student_proficiencies',
            'count_unpracticed_student_proficiencies_end'
          ],
          summaryKey: 'count_unpracticed_student_proficiencies_end',
          sortKey: 'metrics.count_unpracticed_student_proficiencies_end'
        },
        ...LEVELS.map((level) => ({
          index: level + 1,
          label: PROFICIENCY_LEVELS[level],
          metricKeys: [
            `count_student_proficiencies_level_${level}`,
            `count_student_proficiencies_level_${level}_end`
          ],
          summaryKey: `count_student_proficiencies_level_${level}_end`,
          sortKey: `metrics.count_student_proficiencies_level_${level}_end`
        }))
      ]
    },
    // No Not-started bucket: a (student, skill) pair with no measurement is not
    // counted at any level by the backend.
    diagnostic: {
      segments: LEVELS.map((level) => ({
        index: level + 1,
        label: PROFICIENCY_LEVELS[level],
        metricKeys: [`count_student_diagnostic_proficiencies_level_${level}`],
        summaryKey: `count_student_diagnostic_proficiencies_level_${level}`,
        sortKey: `metrics.count_student_diagnostic_proficiencies_level_${level}`
      }))
    }
  };

/** Every slot in the shared segment array, whether or not a method fills it. */
export const PROFICIENCY_SEGMENT_SLOTS = [0, 1, 2, 3, 4];

/** The sort options a method offers, in segment order. */
export const getProficiencySorts = (method: ProficiencyMethod) =>
  PROFICIENCY_SEGMENTS_BY_METHOD[method].segments.map(({label, formalLabel, sortKey}) => ({
    label: formalLabel ?? label,
    sortKey
  }));

/** Metric keys the summary donut reads for a method, in slice order. */
export const getSummaryChartKeys = (method: ProficiencyMethod) =>
  PROFICIENCY_SEGMENTS_BY_METHOD[method].segments.map((segment) => segment.summaryKey);

/** The measured levels only — practice's Not-started bucket is not one. */
export const getProficiencyLevelSegments = (method: ProficiencyMethod) =>
  PROFICIENCY_SEGMENTS_BY_METHOD[method].segments.filter((segment) => segment.index > 0);

/** Reads a segment's count, falling back through its metric keys. */
export const getSegmentCount = (metrics: object, segment: ProficiencySegment): number =>
  segment.metricKeys
    .map((key) => (metrics as Record<string, number | null | undefined>)[key])
    .find((value) => value != null) ?? 0;
